// What was scanned on the order in the warehouse app (app 0.8.0): per item,
// the units scanned while picking and checked after, with the last time and
// who; the order's shipping labels scanned; and every scan (time, who, step,
// code, what it meant). Store logins only: warehouse logins, and servers
// without scanning, show nothing here.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { MdKeyboardArrowDown, MdQrCodeScanner } from "react-icons/md";

import { fetchOrderScans } from "../../../../../../services/orderScans.services";
import { isShipperRole } from "../../../../../../helpers/session";
import {
  STAGE_WORDS,
  lineScans,
  ofText,
  resultText,
  resultTone,
  scanExtras,
  timeText,
} from "./orderScansModel";

const TONE_CLASS = {
  success: "bg-state-success-bg text-state-success-text",
  danger: "bg-state-danger-bg text-state-danger-text",
  warning: "bg-state-warning-bg text-state-warning-text",
  neutral: "bg-state-neutral-bg text-state-neutral-text",
};

const SHOWN = 15;

const CountCell = ({ count, ordered }) =>
  count ? (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
        count.units >= ordered ? TONE_CLASS.success : TONE_CLASS.warning
      }`}
      title={[timeText(count.at), count.by].filter(Boolean).join(" · ")}
    >
      {count.units >= ordered ? "(scanned) " : ""}
      {ofText(count.units, ordered)}
    </span>
  ) : (
    <span className="text-[11px] text-ui-text-subtle">—</span>
  );

export default function OrderScans({ data }) {
  // The signed-in role, read once (no store around it: nothing shows).
  const redux = useContext(ReactReduxContext);
  const role = redux?.store?.getState()?.user?.role;
  const orderId = data?.id;
  const warehouseId = data?.warehouse_id;
  const enabled =
    isShipperRole(role) && Boolean(orderId) && Boolean(warehouseId);
  const [state, setState] = useState({
    loading: true,
    scans: [],
    labels: null,
    failed: false,
  });
  const [open, setOpen] = useState(true);
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setState({ loading: true, scans: [], labels: null, failed: false });
    fetchOrderScans(orderId, warehouseId).then(({ data: response, error }) => {
      if (!active) return;
      if (error) {
        setState({ loading: false, scans: [], labels: null, failed: true });
        return;
      }
      setState({
        loading: false,
        scans: response?.result?.scans || [],
        labels: response?.result?.labels || null,
        failed: false,
      });
    });
    return () => {
      active = false;
    };
  }, [enabled, orderId, warehouseId]);

  const lines = useMemo(
    () => lineScans(data?.order_details, state.scans),
    [data?.order_details, state.scans],
  );

  if (!enabled || state.loading || state.failed) return null;

  const labels = state.labels;
  const shown = all ? state.scans : state.scans.slice(0, SHOWN);

  return (
    <section className="shrink-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-ui-row-hover"
      >
        <span className="flex items-center gap-2 text-[13px] font-semibold text-ui-text">
          <MdQrCodeScanner size={16} />
          Scans in the warehouse app
          {state.scans.length > 0 && (
            <span className="rounded bg-state-neutral-bg px-1.5 py-0.5 text-[10.5px] font-medium text-state-neutral-text">
              {state.scans.length}
            </span>
          )}
          {labels && (labels.total || labels.scanned) ? (
            <span className="text-[11.5px] font-normal text-ui-text-muted">
              · labels scanned {ofText(labels.scanned || 0, labels.total)}
              {labels.shipped
                ? ` · logged leaving ${ofText(labels.shipped, labels.total)}`
                : ""}
            </span>
          ) : null}
        </span>
        <MdKeyboardArrowDown
          size={18}
          className={`shrink-0 text-ui-text-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-ui-border px-3 py-2">
          {state.scans.length === 0 ? (
            <p className="m-0 py-2 text-[12px] text-ui-text-subtle">
              Nothing scanned yet. Pickers scan each item (and the shipping
              labels) with the warehouse app.
            </p>
          ) : (
            <>
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr className="text-left text-[11px] text-ui-text-muted">
                    <th className="py-1 pr-2 font-medium">Item</th>
                    <th className="py-1 pr-2 font-medium">
                      Scanned while picking
                    </th>
                    <th className="py-1 pr-2 font-medium">Checked</th>
                    <th className="py-1 font-medium">Last scan</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const last = [line.check, line.pick, line.load]
                      .filter(Boolean)
                      .sort((a, b) =>
                        String(b.at).localeCompare(String(a.at)),
                      )[0];
                    return (
                      <tr key={line.id} className="border-t border-ui-border">
                        <td className="py-1.5 pr-2 text-ui-text">
                          {line.name}
                          {line.sku ? (
                            <span className="ml-1 text-ui-text-subtle">
                              {line.sku}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1.5 pr-2">
                          <CountCell count={line.pick} ordered={line.ordered} />
                        </td>
                        <td className="py-1.5 pr-2">
                          <CountCell
                            count={line.check}
                            ordered={line.ordered}
                          />
                        </td>
                        <td className="py-1.5 text-ui-text-muted">
                          {last
                            ? [timeText(last.at), last.by]
                                .filter(Boolean)
                                .join(" · ")
                            : "Not scanned"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <h4 className="m-0 mt-3 text-[12px] font-semibold text-ui-text">
                Every scan
              </h4>
              <ol className="m-0 mt-1 list-none p-0">
                {shown.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-baseline gap-x-2 border-t border-ui-border py-1 text-[12px]"
                  >
                    <span className="shrink-0 text-ui-text-subtle">
                      {timeText(row.at)}
                    </span>
                    <span className="text-ui-text">{row.by?.name ?? "-"}</span>
                    <span className="text-ui-text-muted">
                      {STAGE_WORDS[row.stage] ?? row.stage}
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[resultTone(row.result)]}`}
                    >
                      {resultText(row)}
                    </span>
                    {row.line?.name ? (
                      <span className="text-ui-text">{row.line.name}</span>
                    ) : null}
                    {scanExtras(row).map((extra) => (
                      <span
                        key={extra}
                        className={`rounded px-1.5 py-0.5 text-[11px] ${
                          extra === "scanned before"
                            ? TONE_CLASS.warning
                            : TONE_CLASS.neutral
                        }`}
                      >
                        {extra}
                      </span>
                    ))}
                    <code className="text-[11px] text-ui-text-subtle">
                      {row.code}
                    </code>
                  </li>
                ))}
              </ol>
              {state.scans.length > SHOWN && (
                <button
                  type="button"
                  onClick={() => setAll((value) => !value)}
                  className="mt-1 text-[12px] font-medium text-brand hover:underline"
                >
                  {all ? "Show fewer" : `Show all ${state.scans.length}`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
